import { useCallback, useEffect, useState } from 'react';

// localStorage shape: { 'ko': 'v2', 'ja': 'v1', ... }.
// Source of truth lives on the User document; this cache exists so synchronous
// surfaces (Navbar, HomePage CTA) can route conditionally without an API call.
const STORAGE_KEY = 'curriculumPreferences';
const AVAILABLE_KEY = 'curriculumV2AvailableTargets';
const CHANGE_EVENT = 'curriculumPreferencesChanged';

function readPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_) {
    return {};
  }
}

function readAvailableTargets() {
  try {
    const raw = localStorage.getItem(AVAILABLE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch (_) {
    return null;
  }
}

// v2 is the default. A target resolves to v2 unless v2 is known to be
// unavailable for it. Before the profile hydrates the availability list we
// stay optimistic (default v2), since every supported target has v2 content.
function v2AvailableFor(target) {
  if (!target) return false;
  const list = readAvailableTargets();
  if (!list) return true;
  return list.includes(target);
}

function readTarget() {
  return (localStorage.getItem('targetLanguage') || '').toLowerCase();
}

// The v2 default applies only to authenticated, non-guest learners: the v2
// session requires an account, so guests stay on v1 (their prior routing).
function isAuthedLearner() {
  try {
    return !!localStorage.getItem('token') && localStorage.getItem('guestMode') !== 'true';
  } catch (_) {
    return false;
  }
}

function compute() {
  const target = readTarget();
  const explicit = target ? (readPrefs()[target] || null) : null;
  let version = explicit;
  if (!version && target && isAuthedLearner()) {
    version = v2AvailableFor(target) ? 'v2' : 'v1';
  }
  return {
    target,
    version,
    isV2: version === 'v2',
    isV1: version === 'v1',
    // Whether the user made an explicit choice (drives Settings UI, not routing).
    isUnset: !explicit,
  };
}

export function useCurriculumVersion() {
  const get = useCallback(compute, []);
  const [state, setState] = useState(get);
  useEffect(() => {
    const sync = () => setState(get());
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener('targetLanguageChanged', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener('targetLanguageChanged', sync);
      window.removeEventListener('storage', sync);
    };
  }, [get]);
  return state;
}

export function setCurriculumPreferenceCache(targetLanguage, version) {
  if (!targetLanguage) return;
  const prefs = readPrefs();
  prefs[String(targetLanguage).toLowerCase()] = version;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

export function hydrateCurriculumPreferences(curriculumV2) {
  if (!curriculumV2) return;
  if (curriculumV2.preferences) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(curriculumV2.preferences));
  }
  if (Array.isArray(curriculumV2.availableTargets)) {
    localStorage.setItem(AVAILABLE_KEY, JSON.stringify(curriculumV2.availableTargets));
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

export function clearCurriculumPreferenceCache() {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(AVAILABLE_KEY);
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}
